import { Button } from "flowbite-react";
import React, { useContext, useState } from "react";
import apiRequest from "../utils/apiRequest";
import { Store } from "../context/store";
// Assuming you have access to Modal, Button, Input, toast, and apiRequest
// import { Modal, Button, Input } from './components/ui';

// Simple local fallback toast implementation to avoid 'toast' undefined errors.
// Replace this with your app's notification/toast utility (e.g., react-hot-toast, Flowbite Toast) as needed.
const toast = ({ title, description, variant }) => {
  const message = title
    ? `${title}${description ? " - " + description : ""}`
    : description || "";
  if (variant === "destructive") {
    // treat as error
    console.error(message);
    try {
      window.alert(message);
    } catch (e) {
      /* noop */
    }
  } else {
    // success/info
    console.log(message);
    try {
      window.alert(message);
    } catch (e) {
      /* noop */
    }
  }
};

const PaymentReviewModal = ({
  payment,
  onClose,
  onRequestProcessed,
  commissionId,
}) => {
  const [status, setStatus] = useState("confirmed"); // Default to confirmed
  const [commissionEarned, setCommissionEarned] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const store = useContext(Store);
  const [baseUrl] = store.url;

  // Assuming 'token' is accessible via context or passed as a prop
  const token = localStorage.getItem("royal-token");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (
      status === "confirmed" &&
      (!commissionEarned || Number(commissionEarned) <= 0)
    ) {
      setError(
        "Commission earned must be greater than 0 for confirmed payments.",
      );
      return;
    }

    // Additional validation (e.g., ensure commissionEarned is a number)

    setIsProcessing(true);
    try {
      const payload = {
        status, // 'confirmed' or 'rejected'
        commissionEarned: status === "confirmed" ? Number(commissionEarned) : 0,
      };

      // API endpoint to process the specific customer payment
      //   /process/:paymentId/commission/:id
      const url = `${baseUrl}/process/${payment._id}/commission/${commissionId}`;
      // Assuming payment._id is the unique ID for the customer payment record

      await apiRequest(url, "PUT", payload, token);

      toast({
        title: "Payment Processed",
        description: `Customer payment was ${status} successfully.`,
      });

      onRequestProcessed();
      onClose();
    } catch (err) {
      console.log(err, "xxxxxx");
      setError(err.message || "Failed to process payment.");
      toast({
        title: "Error",
        description: "Failed to process payment.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-lg p-6 bg-white dark:bg-gray-800 rounded-lg shadow-2xl">
        <h3 className="text-xl font-bold mb-4">Review Customer Payment</h3>

        {/* Payment Details Preview */}
        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md mb-4 text-sm">
          <p>
            <strong>Amount:</strong>{" "}
            {payment.amountPaid.toLocaleString("en-US", {
              style: "currency",
              currency: "NGN",
            })}
          </p>
          <p>
            <strong>Paid On:</strong>{" "}
            {new Date(payment.datePaid).toLocaleDateString()}
          </p>
          {/* View Proof Link (assuming payment.proof is available) */}
          {payment.proof && (
            <a
              href={payment.proof}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline flex items-center gap-1 mt-1"
            >
              View Proof of Payment 🔗
            </a>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Status Selection (Radio Buttons) */}
          <div className="space-y-2">
            <label className="font-medium text-sm block">Decision</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="status"
                  value="confirmed"
                  checked={status === "confirmed"}
                  onChange={() => setStatus("confirmed")}
                />
                Confirm
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="status"
                  value="rejected"
                  checked={status === "rejected"}
                  onChange={() => {
                    setStatus("rejected");
                    setCommissionEarned(""); // Clear commission if rejecting
                  }}
                />
                Reject
              </label>
            </div>
          </div>

          {/* Commission Earned Input (Only for Confirmed Status) */}
          {status === "confirmed" && (
            <div className="space-y-2">
              <label
                htmlFor="commissionEarned"
                className="font-medium text-sm block"
              >
                Commission Earned from this Payment (NGN)
              </label>
              <input
                id="commissionEarned"
                type="number"
                step="0.01"
                value={commissionEarned}
                onChange={(e) => setCommissionEarned(e.target.value)}
                placeholder="e.g., 50000"
                className="w-full p-2 border rounded-md"
                required
              />
            </div>
          )}

          {error && <p className="text-red-500 text-sm">{error}</p>}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isProcessing}>
              {isProcessing ? "Processing..." : `Submit`}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentReviewModal;
